import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Business as VendorIcon,
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ProcurementItems = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view, stock, price
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: '',
    lowStock: false
  });
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    totalValue: 0,
    averagePrice: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    specifications: {
      material: '',
      dimensions: '',
      weight: '',
      color: '',
      brand: '',
      model: ''
    },
    preferredVendors: [],
    pricing: {
      currentPrice: 0,
      currency: 'USD',
      priceHistory: []
    },
    inventory: {
      currentStock: 0,
      unit: 'pcs',
      minimumStock: 10,
      maximumStock: 1000,
      reorderPoint: 20,
      location: ''
    },
    usage: {
      averageMonthlyUsage: 0,
      lastUsedDate: null,
      totalUsed: 0
    },
    compliance: {
      certifications: [],
      standards: [],
      safetyRequirements: ''
    },
    qualityControl: {
      inspectionRequired: false,
      qualityStandards: '',
      testingProcedures: ''
    },
    status: 'active',
    notes: ''
  });

  const [stockUpdate, setStockUpdate] = useState({
    quantity: 0,
    type: 'add', // add, remove, set
    reason: '',
    location: ''
  });

  const [priceUpdate, setPriceUpdate] = useState({
    price: 0,
    vendor: '',
    effectiveDate: new Date(),
    notes: ''
  });

  const categories = [
    'Construction Materials',
    'Tools & Equipment',
    'Electrical',
    'Plumbing',
    'HVAC',
    'Safety Equipment',
    'Hardware',
    'Finishing Materials',
    'Concrete & Masonry',
    'Steel & Metal',
    'Wood & Lumber',
    'Insulation',
    'Roofing',
    'Other'
  ];

  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet', 'bag', 'ton'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
  const statusOptions = [
    { value: 'active', label: 'Active', color: 'success' },
    { value: 'inactive', label: 'Inactive', color: 'default' },
    { value: 'discontinued', label: 'Discontinued', color: 'error' },
    { value: 'pending_approval', label: 'Pending Approval', color: 'warning' }
  ];

  useEffect(() => {
    fetchItems();
    fetchVendors();
    fetchStats();
  }, [filters]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.lowStock) params.append('lowStock', 'true');
      
      const response = await api.get(`/procurement-items?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      setError('Failed to fetch procurement items');
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors?status=active');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/procurement-items/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching item stats:', error);
    }
  };

  const handleOpenDialog = (mode, item = null) => {
    setDialogMode(mode);
    setSelectedItem(item);
    setActiveTab(0);
    
    if (item && mode !== 'create') {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category: item.category || '',
        subcategory: item.subcategory || '',
        specifications: item.specifications || {
          material: '',
          dimensions: '',
          weight: '',
          color: '',
          brand: '',
          model: ''
        },
        preferredVendors: item.preferredVendors?.map(v => v._id || v) || [],
        pricing: item.pricing || {
          currentPrice: 0,
          currency: 'USD',
          priceHistory: []
        },
        inventory: item.inventory || {
          currentStock: 0,
          unit: 'pcs',
          minimumStock: 10,
          maximumStock: 1000,
          reorderPoint: 20,
          location: ''
        },
        usage: item.usage || {
          averageMonthlyUsage: 0,
          lastUsedDate: null,
          totalUsed: 0
        },
        compliance: item.compliance || {
          certifications: [],
          standards: [],
          safetyRequirements: ''
        },
        qualityControl: item.qualityControl || {
          inspectionRequired: false,
          qualityStandards: '',
          testingProcedures: ''
        },
        status: item.status || 'active',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category: '',
        subcategory: '',
        specifications: {
          material: '',
          dimensions: '',
          weight: '',
          color: '',
          brand: '',
          model: ''
        },
        preferredVendors: [],
        pricing: {
          currentPrice: 0,
          currency: 'USD',
          priceHistory: []
        },
        inventory: {
          currentStock: 0,
          unit: 'pcs',
          minimumStock: 10,
          maximumStock: 1000,
          reorderPoint: 20,
          location: ''
        },
        usage: {
          averageMonthlyUsage: 0,
          lastUsedDate: null,
          totalUsed: 0
        },
        compliance: {
          certifications: [],
          standards: [],
          safetyRequirements: ''
        },
        qualityControl: {
          inspectionRequired: false,
          qualityStandards: '',
          testingProcedures: ''
        },
        status: 'active',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setStockUpdate({
      quantity: 0,
      type: 'add',
      reason: '',
      location: ''
    });
    setPriceUpdate({
      price: 0,
      vendor: '',
      effectiveDate: new Date(),
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await api.post('/procurement-items', formData);
        setSuccess('Procurement item created successfully');
      } else if (dialogMode === 'edit') {
        await api.put(`/procurement-items/${selectedItem._id}`, formData);
        setSuccess('Procurement item updated successfully');
      } else if (dialogMode === 'stock') {
        await api.post(`/procurement-items/${selectedItem._id}/stock`, stockUpdate);
        setSuccess('Stock updated successfully');
      } else if (dialogMode === 'price') {
        await api.post(`/procurement-items/${selectedItem._id}/price`, priceUpdate);
        setSuccess('Price updated successfully');
      }
      
      handleCloseDialog();
      fetchItems();
      fetchStats();
    } catch (error) {
      setError('Failed to save procurement item');
      console.error('Error saving item:', error);
    }
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this procurement item?')) {
      try {
        await api.delete(`/procurement-items/${itemId}`);
        setSuccess('Procurement item deleted successfully');
        fetchItems();
        fetchStats();
      } catch (error) {
        setError('Failed to delete procurement item');
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.label : status;
  };

  const isLowStock = (item) => {
    return item.inventory?.currentStock <= item.inventory?.reorderPoint;
  };

  const getStockStatus = (item) => {
    const current = item.inventory?.currentStock || 0;
    const reorder = item.inventory?.reorderPoint || 0;
    const minimum = item.inventory?.minimumStock || 0;
    
    if (current <= minimum) return { status: 'critical', color: 'error', label: 'Critical' };
    if (current <= reorder) return { status: 'low', color: 'warning', label: 'Low Stock' };
    return { status: 'good', color: 'success', label: 'Good' };
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading procurement items...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Procurement Items
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/procurement-items/create')}
        >
          Add Item
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InventoryIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Items
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalItems}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Badge badgeContent={stats.lowStockItems} color="error">
                  <WarningIcon color="warning" sx={{ mr: 2 }} />
                </Badge>
                <Box sx={{ ml: 1 }}>
                  <Typography color="textSecondary" gutterBottom>
                    Low Stock Items
                  </Typography>
                  <Typography variant="h5">
                    {stats.lowStockItems}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Value
                  </Typography>
                  <Typography variant="h5">
                    ${stats.totalValue?.toLocaleString() || '0'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Average Price
                  </Typography>
                  <Typography variant="h5">
                    ${stats.averagePrice?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search Items"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  label="Category"
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant={filters.lowStock ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => setFilters(prev => ({ ...prev, lowStock: !prev.lowStock }))}
                startIcon={<WarningIcon />}
              >
                Low Stock
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({ category: '', status: '', search: '', lowStock: false })}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Current Stock</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Stock Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const stockStatus = getStockStatus(item);
                  return (
                    <TableRow key={item._id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.inventory?.currentStock || 0} {item.inventory?.unit || 'pcs'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Min: {item.inventory?.minimumStock || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${item.pricing?.currentPrice?.toFixed(2) || '0.00'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {item.pricing?.currency || 'USD'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(item.status)}
                          size="small"
                          color={getStatusColor(item.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={stockStatus.label}
                          size="small"
                          color={stockStatus.color}
                          icon={stockStatus.status === 'critical' ? <ErrorIcon /> : 
                                stockStatus.status === 'low' ? <WarningIcon /> : <CheckIcon />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', item)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', item)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Stock">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog('stock', item)}
                          >
                            <InventoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Price">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenDialog('price', item)}
                          >
                            <MoneyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Item Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add Procurement Item' : 
           dialogMode === 'edit' ? 'Edit Procurement Item' : 
           dialogMode === 'stock' ? 'Update Stock' :
           dialogMode === 'price' ? 'Update Price' : 'Item Details'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'stock' ? (
            // Stock Update Mode
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Update Stock for: {selectedItem?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Current Stock: {selectedItem?.inventory?.currentStock || 0} {selectedItem?.inventory?.unit || 'pcs'}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={stockUpdate.type}
                      label="Action"
                      onChange={(e) => setStockUpdate(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <MenuItem value="add">Add Stock</MenuItem>
                      <MenuItem value="remove">Remove Stock</MenuItem>
                      <MenuItem value="set">Set Stock</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={stockUpdate.quantity}
                    onChange={(e) => setStockUpdate(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={stockUpdate.location}
                    onChange={(e) => setStockUpdate(prev => ({ ...prev, location: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reason"
                    value={stockUpdate.reason}
                    onChange={(e) => setStockUpdate(prev => ({ ...prev, reason: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : dialogMode === 'price' ? (
            // Price Update Mode
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Update Price for: {selectedItem?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Current Price: ${selectedItem?.pricing?.currentPrice?.toFixed(2) || '0.00'}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Price"
                    type="number"
                    value={priceUpdate.price}
                    onChange={(e) => setPriceUpdate(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ step: '0.01', min: '0' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={vendors}
                    getOptionLabel={(option) => option.name}
                    value={vendors.find(v => v._id === priceUpdate.vendor) || null}
                    onChange={(event, newValue) => {
                      setPriceUpdate(prev => ({ ...prev, vendor: newValue?._id || '' }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Vendor" fullWidth />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Effective Date"
                    value={priceUpdate.effectiveDate}
                    onChange={(date) => setPriceUpdate(prev => ({ ...prev, effectiveDate: date }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={priceUpdate.notes}
                    onChange={(e) => setPriceUpdate(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Create/Edit/View Mode
            <Box sx={{ mt: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Basic Info" />
                <Tab label="Specifications" />
                <Tab label="Inventory" />
                <Tab label="Pricing" />
                <Tab label="Compliance" />
              </Tabs>
              
              {/* Basic Info Tab */}
              {activeTab === 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Item Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formData.category}
                        label="Category"
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subcategory"
                      value={formData.subcategory}
                      onChange={(e) => handleInputChange('subcategory', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={vendors}
                      getOptionLabel={(option) => option.name}
                      value={vendors.filter(vendor => formData.preferredVendors.includes(vendor._id))}
                      onChange={(event, newValue) => {
                        handleInputChange('preferredVendors', newValue.map(v => v._id));
                      }}
                      disabled={dialogMode === 'view'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Preferred Vendors"
                          placeholder="Select preferred vendors"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Specifications Tab */}
              {activeTab === 1 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Material"
                      value={formData.specifications.material}
                      onChange={(e) => handleInputChange('specifications.material', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dimensions"
                      value={formData.specifications.dimensions}
                      onChange={(e) => handleInputChange('specifications.dimensions', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Weight"
                      value={formData.specifications.weight}
                      onChange={(e) => handleInputChange('specifications.weight', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Color"
                      value={formData.specifications.color}
                      onChange={(e) => handleInputChange('specifications.color', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Brand"
                      value={formData.specifications.brand}
                      onChange={(e) => handleInputChange('specifications.brand', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Model"
                      value={formData.specifications.model}
                      onChange={(e) => handleInputChange('specifications.model', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Inventory Tab */}
              {activeTab === 2 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current Stock"
                      type="number"
                      value={formData.inventory.currentStock}
                      onChange={(e) => handleInputChange('inventory.currentStock', parseInt(e.target.value) || 0)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={formData.inventory.unit}
                        label="Unit"
                        onChange={(e) => handleInputChange('inventory.unit', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {units.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Minimum Stock"
                      type="number"
                      value={formData.inventory.minimumStock}
                      onChange={(e) => handleInputChange('inventory.minimumStock', parseInt(e.target.value) || 0)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Maximum Stock"
                      type="number"
                      value={formData.inventory.maximumStock}
                      onChange={(e) => handleInputChange('inventory.maximumStock', parseInt(e.target.value) || 0)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Reorder Point"
                      type="number"
                      value={formData.inventory.reorderPoint}
                      onChange={(e) => handleInputChange('inventory.reorderPoint', parseInt(e.target.value) || 0)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Storage Location"
                      value={formData.inventory.location}
                      onChange={(e) => handleInputChange('inventory.location', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Pricing Tab */}
              {activeTab === 3 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current Price"
                      type="number"
                      value={formData.pricing.currentPrice}
                      onChange={(e) => handleInputChange('pricing.currentPrice', parseFloat(e.target.value) || 0)}
                      disabled={dialogMode === 'view'}
                      inputProps={{ step: '0.01', min: '0' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={formData.pricing.currency}
                        label="Currency"
                        onChange={(e) => handleInputChange('pricing.currency', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency} value={currency}>
                            {currency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Price History Chart */}
                  {selectedItem?.pricing?.priceHistory?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Price History
                      </Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={selectedItem.pricing.priceHistory}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(value) => new Date(value).toLocaleDateString()}
                            />
                            <YAxis />
                            <RechartsTooltip 
                              labelFormatter={(value) => new Date(value).toLocaleDateString()}
                              formatter={(value) => [`$${value.toFixed(2)}`, 'Price']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="price" 
                              stroke="#8884d8" 
                              strokeWidth={2}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
              
              {/* Compliance Tab */}
              {activeTab === 4 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Certifications (comma-separated)"
                      value={formData.compliance.certifications.join(', ')}
                      onChange={(e) => handleInputChange('compliance.certifications', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Standards (comma-separated)"
                      value={formData.compliance.standards.join(', ')}
                      onChange={(e) => handleInputChange('compliance.standards', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Safety Requirements"
                      multiline
                      rows={3}
                      value={formData.compliance.safetyRequirements}
                      onChange={(e) => handleInputChange('compliance.safetyRequirements', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Quality Standards"
                      multiline
                      rows={2}
                      value={formData.qualityControl.qualityStandards}
                      onChange={(e) => handleInputChange('qualityControl.qualityStandards', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Testing Procedures"
                      multiline
                      rows={2}
                      value={formData.qualityControl.testingProcedures}
                      onChange={(e) => handleInputChange('qualityControl.testingProcedures', e.target.value)}
                      disabled={dialogMode === 'view'}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 
               dialogMode === 'edit' ? 'Update' : 
               dialogMode === 'stock' ? 'Update Stock' :
               dialogMode === 'price' ? 'Update Price' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcurementItems;